/**
 * PillarCard Component
 * ESG Pillar card with progress ring, score, and topic count
 */
import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import Svg, { Circle, Defs, LinearGradient, Stop } from 'react-native-svg';
import { DesktopTheme, getPillarColor, getPillarBgColor } from '../theme/desktopTheme';

// Pillar icons mapping
const PILLAR_ICONS = {
  Environmental: '🌿',
  Social: '👥',
  Governance: '⚖️',
};

const CircularProgressRing = ({
  progress,
  size = 100,
  strokeWidth = 8,
  color,
  backgroundColor = '#e2e8f0',
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <Svg width={size} height={size}>
      <Defs>
        <LinearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <Stop offset="0%" stopColor={color} stopOpacity="1" />
          <Stop offset="100%" stopColor={color} stopOpacity="0.7" />
        </LinearGradient>
      </Defs>
      {/* Background circle */}
      <Circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={backgroundColor}
        strokeWidth={strokeWidth}
        fill="none"
      />
      {/* Progress circle */}
      <Circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="url(#progressGradient)"
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </Svg>
  );
};

const PillarCard = ({
  pillar,
  score,
  previousScore,
  topicsCompleted,
  totalTopics,
  onPress,
  onExpandPress,
  isExpanded,
  style,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const pillarColor = getPillarColor(pillar);
  const pillarBgColor = getPillarBgColor(pillar);
  const icon = PILLAR_ICONS[pillar] || '📊';

  // Calculate trend
  const trend = previousScore !== undefined && previousScore !== null
    ? score - previousScore
    : null;

  const getTrendIcon = () => {
    if (trend === null || trend === 0) return '→';
    return trend > 0 ? '↑' : '↓';
  };

  const getTrendColor = () => {
    if (trend === null || trend === 0) return colors.textMuted;
    return trend > 0 ? colors.success : colors.error;
  };

  return (
    <TouchableOpacity
      style={[
        styles.card,
        { borderColor: isHovered ? pillarColor : colors.border },
        isHovered && styles.cardHovered,
        style,
      ]}
      onPress={onPress}
      onMouseEnter={Platform.OS === 'web' ? () => setIsHovered(true) : undefined}
      onMouseLeave={Platform.OS === 'web' ? () => setIsHovered(false) : undefined}
      activeOpacity={0.9}
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={[styles.iconContainer, { backgroundColor: pillarBgColor }]}>
          <Text style={styles.icon}>{icon}</Text>
        </View>
        <View style={styles.headerText}>
          <Text style={[styles.pillarName, { color: pillarColor }]}>{pillar}</Text>
          <Text style={styles.topicCount}>
            {topicsCompleted}/{totalTopics} topics
          </Text>
        </View>
      </View>

      {/* Progress Ring */}
      <View style={styles.progressContainer}>
        <CircularProgressRing
          progress={score || 0}
          size={120}
          strokeWidth={10}
          color={pillarColor}
        />
        <View style={styles.scoreOverlay}>
          <Text style={styles.scoreValue}>{Math.round(score || 0)}</Text>
          <Text style={styles.scoreLabel}>%</Text>
        </View>
      </View>

      {/* Trend */}
      {trend !== null && (
        <View style={styles.trendContainer}>
          <Text style={[styles.trendIcon, { color: getTrendColor() }]}>
            {getTrendIcon()}
          </Text>
          <Text style={[styles.trendValue, { color: getTrendColor() }]}>
            {trend > 0 ? '+' : ''}{Math.round(trend)}%
          </Text>
          <Text style={styles.trendLabel}> from last quarter</Text>
        </View>
      )}

      {/* Expand Button */}
      <TouchableOpacity
        style={[styles.expandButton, { backgroundColor: pillarBgColor }]}
        onPress={onExpandPress}
      >
        <Text style={[styles.expandText, { color: pillarColor }]}>
          {isExpanded ? 'Hide Topics' : 'View Topics'}
        </Text>
        <Text style={[styles.expandIcon, { color: pillarColor }]}>
          {isExpanded ? '▲' : '▼'}
        </Text>
      </TouchableOpacity>

      {/* Top accent */}
      <View style={[styles.accentBar, { backgroundColor: pillarColor }]} />
    </TouchableOpacity>
  );
};

const { colors, typography, spacing, borderRadius, shadows } = DesktopTheme;

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.cardBg,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    position: 'relative',
    overflow: 'hidden',
    minWidth: 280,
    ...shadows.card,
    borderWidth: 1,
  },
  cardHovered: {
    ...shadows.md,
    transform: [{ translateY: -4 }],
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  icon: {
    fontSize: 24,
  },
  headerText: {
    flex: 1,
  },
  pillarName: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h4,
  },
  topicCount: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textSecondary,
    marginTop: 2,
  },

  // Progress
  progressContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: spacing.md,
    position: 'relative',
  },
  scoreOverlay: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scoreValue: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h1,
    color: colors.textPrimary,
  },
  scoreLabel: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textSecondary,
    marginTop: -4,
  },

  // Trend
  trendContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  trendIcon: {
    fontSize: typography.fontSize.body,
    marginRight: 4,
  },
  trendValue: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.body,
  },
  trendLabel: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
  },

  // Expand Button
  expandButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
    borderRadius: borderRadius.md,
    marginTop: spacing.sm,
  },
  expandText: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.small,
    marginRight: spacing.xs,
  },
  expandIcon: {
    fontSize: 10,
  },

  // Accent
  accentBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    borderTopLeftRadius: borderRadius.lg,
    borderTopRightRadius: borderRadius.lg,
  },
});

export default PillarCard;
